import Task from "@homer.core/task";
import FetchError from "./error/fetch.js";
import ResponseError from "./error/response.js";
import AbortError from "./error/abort.js";

const status = {
    100: "Continue",
    101: "Switching Protocols",
    102: "Processing",
    103: "Early Hints",
    200: "OK",
    201: "Created",
    202: "Accepted",
    203: "Non-Authoritative Information",
    204: "No Content",
    205: "Reset Content",
    206: "Partial Content",
    207: "Multi-Status",
    208: "Already Reported",
    218: "This is fine (Apache Web Server)",
    226: "IM Used",
    300: "Multiple Choices",
    301: "Moved Permanently",
    302: "Found",
    303: "See Other",
    304: "Not Modified",
    306: "Switch Proxy",
    307: "Temporary Redirect",
    308: "Resume Incomplete",
    400: "Bad Request",
    401: "Unauthorized",
    402: "Payment Required",
    403: "Forbidden",
    404: "Not Found",
    405: "Method Not Allowed",
    406: "Not Acceptable",
    407: "Proxy Authentication Required",
    408: "Request Timeout",
    409: "Conflict",
    410: "Gone",
    411: "Length Required",
    412: "Precondition Failed",
    413: "Request Entity Too Large",
    414: "Request-URI Too Long",
    415: "Unsupported Media Type",
    416: "Requested Range Not Satisfiable",
    417: "Expectation Failed",
    418: "I'm a teapot",
    419: "Page Expired (Laravel Framework)",
    420: "Method Failure (Spring Framework)",
    421: "Misdirected Request",
    422: "Unprocessable Entity",
    423: "Locked",
    424: "Failed Dependency",
    426: "Upgrade Required",
    428: "Precondition Required",
    429: "Too Many Requests",
    431: "Request Header Fields Too Large",
    440: "Login Time-out",
    444: "Connection Closed Without Response",
    449: "Retry With",
    450: "Blocked by Windows Parental Controls",
    451: "Unavailable For Legal Reasons",
    494: "Request Header Too Large",
    495: "SSL Certificate Error",
    496: "SSL Certificate Required",
    497: "HTTP Request Sent to HTTPS Port",
    498: "Invalid Token (Esri)",
    499: "Client Closed Request",
    500: "Internal Server Error",
    501: "Not Implemented",
    502: "Bad Gateway",
    503: "Service Unavailable",
    504: "Gateway Timeout",
    505: "HTTP Version Not Supported",
    506: "Variant Also Negotiates",
    507: "Insufficient Storage",
    508: "Loop Detected",
    509: "Bandwidth Limit Exceeded",
    510: "Not Extended",
    511: "Network Authentication Required",
    520: "Unknown Error",
    521: "Web Server Is Down",
    522: "Connection Timed Out",
    523: "Origin Is Unreachable",
    524: "A Timeout Occurred",
    525: "SSL Handshake Failed",
    526: "Invalid SSL Certificate",
    527: "Railgun Listener to Origin Error",
    530: "Origin DNS Error",
    598: "Network Read Timeout Error",
};

function task(identifier, resolve, reject, url, request = {}, cache = false, credentials = false, prioritize = false) {
    const method = request.method || "GET";
    const headers = new Headers(request.header);
    const controller = new AbortController();
    const hasBody = ["POST", "PUT", "DELETE", "PATCH"].includes(method);

    url = new URL(url);
    url.hash = "";
    request.url = url.toString();

    const processResponse = async (response) => {
        if (controller?.signal?.aborted) {
            return {
                state: "aborted",
                url: url.href,
                aborted: !!controller.signal.aborted,
                reason: controller.signal.reason || "unknown",
                request,
            };
        }

        if (response instanceof Error) {
            return {
                state: "error",
                url: url.href,
                name: response.name || "Error",
                message: response.message || "unknown",
                request,
            };
        }

        const headers = response?.headers;
        const type = (headers && headers.get("content-type")) || "";

        return {
            state: "response",
            url: url.href,
            aborted: !!controller?.signal?.aborted,
            status: response?.status || null,
            message: response?.statusText || null,
            type: type || null,
            headers: (headers && Object.fromEntries(headers.entries())) || null,
            data:
                (response &&
                    (type.indexOf("application/json") === 0
                        ? JSON.parse((await response.text()) || "null")
                        : await response.text())) ||
                null,
            request,
        };
    };

    addEventListener("message", (message) => {
        const [uuid, type, signal, additional = {}] = message.data || [];

        if (uuid !== identifier) {
            return;
        }

        if (type !== "signal") {
            return;
        }

        if (signal === "abort") {
            request.aborted = true;
            request.reason = additional?.reason || undefined;

            controller.abort(additional?.reason || undefined);
        }
    });

    if (!hasBody && request.data) {
        url.search = !url.search ? `?${request.data}` : `${url.search}&${request.data}`;
        url.searchParams.sort();
    }

    void fetch(url.href, {
        method,
        headers,
        priority: prioritize ? "high" : "auto",
        body: hasBody ? request.data : undefined,
        cache: cache !== true ? "no-store" : undefined,
        redirect: "follow",
        // NEVER set this to true as keepalive `true` forces
        // the request/body to be limited to ~64 kibibytes
        // which will, e.g., cause file-uploads to fail!
        keepalive: false,
        credentials: credentials === true ? "include" : "same-origin",
        signal: controller.signal,
        referrer: headers?.get("referer") || undefined,
    })
        .then(async (response) => {
            const value = await processResponse(response);
            const accept = headers.get("Accept");
            const isValid = response.ok && (!accept || new RegExp(`^${accept}`).test(value.type || ""));

            switch (isValid) {
                case true:
                    resolve(value);

                    break;
                default:
                    reject(value);

                    break;
            }
        })
        .catch(async (error) => {
            reject(await processResponse(error));
        });

    if (request.aborted) {
        controller.abort(request?.reason || undefined);
    }
}

class ExtendedTask extends Task {
    execute(...parameter) {
        const result = super.execute(...parameter);

        return Object.defineProperties(
            result.catch((result) => {
                switch (result.state) {
                    case "response":
                        throw new ResponseError(
                            result.status
                                ? `${result.status} ${result.message || status[result.status] || "unknown"}`
                                : "unknown",
                            result,
                        );
                    case "aborted":
                        throw new AbortError(result.reason, result);
                    case "error":
                        throw new FetchError(result.message, result, result.name);
                }
            }),
            {
                uuid: {
                    get: () => result.uuid,
                },
                signal: {
                    value: result.signal,
                },
            },
        );
    }
}

export default Object.defineProperties(
    new ExtendedTask(task, (parameter) => {
        const [_, request = {}] = parameter;

        request.header = request.header || {};
        request.header.Referer = request.header.referer || location.href;

        parameter[1] = request;
    }),
    {
        FetchError: {
            value: FetchError,
            enumerable: true,
        },
        ResponseError: {
            value: ResponseError,
            enumerable: true,
        },
        AbortError: {
            value: AbortError,
            enumerable: true,
        },
    },
);
